import { PEDIGREE } from 'src/components/Constants';

// Single source of truth for every NODE label on the pedigree.
//
// Historically the label list was hard-coded in three places (LabelsMenu's
// `labels`+`GROUPS`, LabelConfigurations' `STATIC_KEYS`, and Constants'
// `defaultLabelFilters`). This module is the one place that declares which
// labels exist, their display title, their sidebar group, and — for the
// per-label bold/italic feature — which formatting toggles each offers. The
// panel, the saved configurations, and the renderer all derive from here so the
// list can never drift between them.
//
// d3-FREE on purpose (mirrors labelText.js): it's imported by both React
// components and the pure label helpers.
//
// NOTE: the chart-behaviour switches (showCarrierMarks / showAnalysisOverlay /
// standardNomenclature, carrierMarkStyle) are NOT node labels and deliberately
// live in their own local definitions in LabelsMenu — they have no text glyph to
// format and are not part of this catalogue.

// The formatting toggles offered for an ordinary text label. Kept an open array
// (not a bold|italic union) so a future style — colour, underline — is additive.
const TEXT_FORMATS = ['bold', 'italic'];

// Ordered list. Order WITHIN a group is preserved by GROUPS_DERIVED, so this
// must match the historical group order exactly to keep the panel unchanged.
export const LABEL_DEFINITIONS = [
  // ── Personal ──────────────────────────────────────────────────────────────
  {
    key: 'firstName',
    title: 'First Name',
    group: 'Personal',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'lastName',
    title: 'Last Name',
    group: 'Personal',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'age',
    title: 'Age',
    group: 'Personal',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'upn',
    title: 'UPN',
    group: 'Personal',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'dob',
    title: 'DoB',
    group: 'Personal',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'dod',
    title: 'DoD',
    group: 'Personal',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'personNote',
    title: 'Person Note',
    group: 'Personal',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'hospitalNumber',
    title: 'Hospital Number',
    group: 'Personal',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'externalPatientId',
    title: 'External Patient ID',
    group: 'Personal',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  // ── Genomic ───────────────────────────────────────────────────────────────
  {
    key: 'phenotypesName',
    title: 'Phenotypes Name',
    group: 'Genomic',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'phenotypesID',
    title: 'Phenotypes ID',
    group: 'Genomic',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'genotypesName',
    title: 'Genotype Symbol',
    group: 'Genomic',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'genotypesID',
    title: 'Genotypes ID',
    group: 'Genomic',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'genotypeCarrierStatus',
    title: 'Carrier Status',
    group: 'Genomic',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  // ── Clinical ──────────────────────────────────────────────────────────────
  {
    key: 'diseaseWithAge',
    title: 'Disease (with age of diagnosis)',
    group: 'Clinical',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'confirmedDiagnosis',
    title: 'Clinically Confirmed Diagnosis',
    group: 'Clinical',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'consultationDate',
    title: 'Date of Consultation',
    group: 'Clinical',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  // ── Tests ─────────────────────────────────────────────────────────────────
  {
    key: 'tests',
    title: 'Test Name',
    group: 'Tests',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'genesTested',
    title: 'Genes Tested',
    group: 'Tests',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'variants',
    title: 'Variants',
    group: 'Tests',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'testResults',
    title: 'Test Result',
    group: 'Tests',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'pedigreeText',
    title: 'Test Comments',
    group: 'Tests',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  // ── Reproductive ──────────────────────────────────────────────────────────
  // Data from pregnancy + fetus records, shown beside a fetus symbol. These annotations used to
  // render UNCONDITIONALLY whenever a repro node existed — there was no way to turn them off.
  // Bringing them into the label system makes them user-controllable like every other label; the
  // defaults in `defaultLabelFilters` are set so today's rendering is preserved until a user
  // toggles something. Only fetus nodes carry the underlying data, so these are inert on people.
  {
    key: 'pregnancyOutcome',
    title: 'Pregnancy Outcome',
    group: 'Reproductive',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'gestationalAge',
    title: 'Gestational Age',
    group: 'Reproductive',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'fetalSexMethod',
    title: 'Fetal Sex Method',
    group: 'Reproductive',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'fetusKaryotype',
    title: 'Karyotype / Finding',
    group: 'Reproductive',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'fetusName',
    title: 'Fetus Name',
    group: 'Reproductive',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  }
];

// Sidebar group order (matches the historical GROUPS array, plus Reproductive appended so the
// existing panel order is unchanged).
export const GROUP_ORDER = [
  'Personal',
  'Genomic',
  'Clinical',
  'Tests',
  'Reproductive'
];

// ── Derived lookups (computed once at module load) ────────────────────────────
export const LABEL_KEYS = LABEL_DEFINITIONS.map((d) => d.key);

export const LABEL_BY_KEY = Object.fromEntries(
  LABEL_DEFINITIONS.map((d) => [d.key, d])
);

// { name, keys } per group, in panel order — drop-in for the old GROUPS const.
export const GROUPS_DERIVED = GROUP_ORDER.map((name) => ({
  name,
  keys: LABEL_DEFINITIONS.filter((d) => d.group === name).map((d) => d.key)
}));

// Default visibility for a key, sourced from Constants (one source for both
// axes). Returns undefined for keys absent from defaultLabelFilters (e.g.
// externalPatientId), which reads as "off" everywhere — matching today.
export const defaultVisibilityFor = (key) => PEDIGREE.defaultLabelFilters[key];

// Whether a key offers any formatting control at all.
export const isFormattable = (key) =>
  (LABEL_BY_KEY[key]?.formattable || []).length > 0;

// The default labelsSet.formatting map — only emits entries for labels whose
// defaultFormat is non-empty, so with all defaults `{}` this is `{}` and adds
// nothing to the working state (keeps flag-off behaviour byte-identical).
export const defaultFormatting = () =>
  Object.fromEntries(
    LABEL_DEFINITIONS.filter(
      (d) => d.defaultFormat && (d.defaultFormat.bold || d.defaultFormat.italic)
    ).map((d) => [d.key, { ...d.defaultFormat }])
  );
